import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import {
  CheckCircle2,
  Eye,
  EyeOff,
  Gauge,
  KeyRound,
  RotateCcw,
  Save,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import {
  getYoutubeKeyStatus,
  validateYoutubeKey,
  saveYoutubeKey,
  revertYoutubeKey,
} from "@/lib/youtube-key.functions";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { APP_TIME_ZONE } from "@/lib/datetime";

export const Route = createFileRoute("/_authenticated/admin/youtube")({
  head: () => ({
    meta: [
      { title: "Chave da API do YouTube · Admin" },
      {
        name: "description",
        content: "Atualize e valide com segurança a chave de integração com o YouTube.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: YoutubeKeyPage,
});

type CheckResult =
  | { ok: true; channelTitle: string | null; latencyMs: number }
  | { ok: false; code: string; message: string };

type QuotaStatus = {
  unitsUsed: number;
  remaining: number;
  dailyLimit: number;
  calls: number;
  exhausted: boolean;
  resetAt: string;
  canSync: boolean;
  canAudit: boolean;
  lastReason: string | null;
};

function formatReset(iso: string): string {
  try {
    return new Intl.DateTimeFormat("pt-BR", {
      timeZone: "America/Sao_Paulo",
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  try {
    return new Intl.DateTimeFormat("pt-BR", {
      timeZone: APP_TIME_ZONE,
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(iso));
  } catch {
    return null;
  }
}

function YoutubeKeyPage() {
  const queryClient = useQueryClient();
  const statusFn = useServerFn(getYoutubeKeyStatus);
  const validateFn = useServerFn(validateYoutubeKey);
  const saveFn = useServerFn(saveYoutubeKey);
  const revertFn = useServerFn(revertYoutubeKey);

  const [apiKey, setApiKey] = useState("");
  const [reveal, setReveal] = useState(false);
  const [result, setResult] = useState<CheckResult | null>(null);

  const { data: status, isLoading } = useQuery({
    queryKey: ["youtube-key-status"],
    queryFn: () => statusFn(),
  });

  const trimmed = apiKey.trim();
  const localError =
    trimmed.length === 0
      ? null
      : trimmed.length < 20
        ? "A chave parece curta demais."
        : !/^[A-Za-z0-9_\-.]+$/.test(trimmed)
          ? "A chave contém caracteres inválidos."
          : null;
  const canSubmit = trimmed.length >= 20 && !localError;

  const validate = useMutation({
    mutationFn: () => validateFn({ data: { apiKey: trimmed } }),
    onSuccess: (res) => {
      setResult(res as CheckResult);
      if (res.ok) toast.success("Chave válida — pode salvar com segurança.");
      else toast.error(res.message);
    },
    onError: (e: any) => toast.error(String(e?.message ?? e)),
  });

  const save = useMutation({
    mutationFn: () => saveFn({ data: { apiKey: trimmed } }),
    onSuccess: (res) => {
      setResult(res.check as CheckResult);
      if (res.saved) {
        toast.success("Chave validada e salva. A integração já está usando a nova chave.");
        setApiKey("");
        setReveal(false);
        queryClient.invalidateQueries({ queryKey: ["youtube-key-status"] });
      } else {
        toast.error(`Não salvei: ${(res.check as any).message}`);
      }
    },
    onError: (e: any) => toast.error(String(e?.message ?? e)),
  });

  const revert = useMutation({
    mutationFn: () => revertFn(),
    onSuccess: () => {
      toast.success("Chave salva removida. Voltamos à chave original do projeto.");
      setResult(null);
      queryClient.invalidateQueries({ queryKey: ["youtube-key-status"] });
    },
    onError: (e: any) => toast.error(String(e?.message ?? e)),
  });

  const busy = validate.isPending || save.isPending || revert.isPending;
  const activeCheck = status?.check as CheckResult | undefined;
  const quota = (status as { quota?: QuotaStatus } | undefined)?.quota;
  const quotaPct = quota
    ? Math.min(100, Math.round((quota.unitsUsed / Math.max(1, quota.dailyLimit)) * 100))
    : 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Chave da API do YouTube</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          A chave é gravada apenas no servidor da plataforma, nunca aparece por completo nesta tela
          e só é salva depois de ser testada contra a API do YouTube.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="h-4 w-4 text-primary" /> Situação atual
          </CardTitle>
          <CardDescription>
            Chave em uso pela sincronização, monitor ao vivo e página de status.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Verificando…</p>
          ) : !status?.configured ? (
            <p className="text-sm text-destructive">Nenhuma chave configurada.</p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <code className="rounded bg-muted px-2 py-1 font-mono text-sm">
                  {status.masked}
                </code>
                <Badge variant="secondary">
                  {status.source === "database"
                    ? "Definida nesta tela"
                    : "Chave original do projeto"}
                </Badge>
                {activeCheck?.ok ? (
                  <Badge className="gap-1">
                    <CheckCircle2 className="h-3 w-3" /> Funcionando
                  </Badge>
                ) : (
                  <Badge variant="destructive" className="gap-1">
                    <XCircle className="h-3 w-3" /> Com falha
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                {activeCheck?.ok
                  ? `Canal “${activeCheck.channelTitle ?? "—"}” respondeu em ${activeCheck.latencyMs} ms.`
                  : (activeCheck?.message ?? "Não foi possível verificar a chave.")}
              </p>
              {formatDate(status.updatedAt) && (
                <p className="text-xs text-muted-foreground">
                  Atualizada em {formatDate(status.updatedAt)}.
                </p>
              )}
              {status.source === "database" && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => revert.mutate()}
                  className="gap-2"
                >
                  <RotateCcw className="h-4 w-4" /> Voltar à chave original
                </Button>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {quota && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Gauge className="h-4 w-4 text-primary" /> Cota diária da YouTube Data API v3
            </CardTitle>
            <CardDescription>
              Estimativa interna (1 unidade por channels.list, playlistItems.list e videos.list). O
              valor oficial está no Google Cloud Console. A cota reseta à meia-noite no Pacífico.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <>
              <div className="flex flex-wrap items-center gap-2">
                {quota.exhausted ? (
                  <Badge variant="destructive">Cota esgotada</Badge>
                ) : quota.canSync ? (
                  <Badge>Sincronização liberada</Badge>
                ) : (
                  <Badge variant="secondary">Reserva do ao vivo</Badge>
                )}
                <span className="text-sm text-muted-foreground">
                  {quota.unitsUsed} / {quota.dailyLimit} un. · {quota.calls} chamada(s)
                </span>
              </div>
              <div
                className="h-2 overflow-hidden rounded bg-muted"
                role="meter"
                aria-valuemin={0}
                aria-valuemax={quota.dailyLimit}
                aria-valuenow={quota.unitsUsed}
                aria-label="Consumo estimado da cota diária do YouTube"
              >
                <div
                  className={`h-full ${quota.exhausted ? "bg-destructive" : quotaPct >= 75 ? "bg-amber-500" : "bg-primary"}`}
                  style={{ width: `${quotaPct}%` }}
                />
              </div>
              <p className="text-sm text-muted-foreground">
                Reset estimado em {formatReset(quota.resetAt)} (BRT).{" "}
                {quota.exhausted
                  ? "Sincronização e auditoria pausadas; o ao vivo usa o último estado em cache."
                  : quota.canSync
                    ? "Há cota suficiente para sincronizar sem comprometer o monitor ao vivo."
                    : "A sincronização automática fica pausada para preservar o monitor ao vivo."}
              </p>
              {quota.lastReason && (
                <p className="text-xs text-muted-foreground">
                  Último retorno da API: {quota.lastReason}.
                </p>
              )}
            </>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <KeyRound className="h-4 w-4 text-primary" /> Atualizar a chave
          </CardTitle>
          <CardDescription>
            Gere a chave no Google Cloud Console (APIs e serviços → Credenciais) com a{" "}
            <strong>YouTube Data API v3</strong> habilitada.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmit && !busy) save.mutate();
            }}
          >
            <div className="space-y-2">
              <Label htmlFor="yt-key">Nova chave</Label>
              <div className="flex gap-2">
                <Input
                  id="yt-key"
                  type={reveal ? "text" : "password"}
                  value={apiKey}
                  autoComplete="off"
                  spellCheck={false}
                  maxLength={200}
                  placeholder="AIza…"
                  onChange={(e) => {
                    setApiKey(e.target.value);
                    setResult(null);
                  }}
                  className="font-mono"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  aria-label={reveal ? "Ocultar chave" : "Mostrar chave"}
                  onClick={() => setReveal((v) => !v)}
                >
                  {reveal ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              {localError && <p className="text-xs text-destructive">{localError}</p>}
            </div>

            {result && (
              <Alert variant={result.ok ? "default" : "destructive"}>
                <AlertTitle className="flex items-center gap-2">
                  {result.ok ? (
                    <CheckCircle2 className="h-4 w-4" />
                  ) : (
                    <XCircle className="h-4 w-4" />
                  )}
                  {result.ok ? "Chave válida" : "Chave recusada"}
                </AlertTitle>
                <AlertDescription>
                  {result.ok
                    ? `Canal “${result.channelTitle ?? "—"}” encontrado em ${result.latencyMs} ms.`
                    : result.message}
                </AlertDescription>
              </Alert>
            )}

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={!canSubmit || busy}
                onClick={() => validate.mutate()}
                className="gap-2"
              >
                <ShieldCheck className="h-4 w-4" />
                {validate.isPending ? "Testando…" : "Testar sem salvar"}
              </Button>
              <Button type="submit" disabled={!canSubmit || busy} className="gap-2">
                <Save className="h-4 w-4" />
                {save.isPending ? "Validando e salvando…" : "Validar e salvar"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              O salvamento só acontece se o teste passar — uma chave inválida nunca substitui a
              atual.
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
